import { computed } from "vue";
import type { Ref } from "vue";
import { useI18n } from "vue-i18n";

import { getAlmInfo } from "@/backend/read/BackendReader";
import type { BoardType } from "@/model/baseTypes";
import type { Board } from "@/model/board";
import type { BoardCard } from "@/model/card";
import { useServerSettingsStore } from "@/store/serverSettings";

import { canDelete, canRemove, canUnplan, isDeleteBlockedByAlm, isOrigin } from "./eligibility";

/**
 * Composes the bulk-modal subtitle:
 *   "{count} items selected." (always)
 *   + " Only {count} can be {action}." (only when eligible < total)
 *
 * partialKey is the i18n key for the action-specific suffix
 * (e.g., "action.bulkDelete.subtitlePartial").
 */
export function useBulkSubtitle(
  totalCount: Ref<number>,
  eligibleCount: Ref<number>,
  partialKey: string,
) {
  const { t } = useI18n();
  return computed(() => {
    const itemsSelected = t("action.bulk.itemsSelected", totalCount.value);
    if (eligibleCount.value === totalCount.value) return itemsSelected;
    return `${itemsSelected} ${t(partialKey, eligibleCount.value)}`;
  });
}

/** Partition cards by a predicate; `shouldRender` when the "yes" bucket is non-empty. */
function usePartition(
  cards: Ref<BoardCard[]>,
  board: Ref<Board>,
  predicate: (card: BoardCard["data"], board: Board) => boolean,
) {
  const yes = computed(() => cards.value.filter((c) => predicate(c.data, board.value)));
  const no = computed(() => cards.value.filter((c) => !predicate(c.data, board.value)));
  const shouldRender = computed(() => yes.value.length > 0);
  return { shouldRender, yes, no };
}

export function useRemoveEligibility(cards: Ref<BoardCard[]>, board: Ref<Board>) {
  const { shouldRender, yes, no } = usePartition(cards, board, canRemove);
  // Split the blocked bucket so each reason gets its own section + message.
  const notRemovableUnplannable = computed(() =>
    no.value.filter((c) => canUnplan(c.data, board.value)),
  );
  const notRemovableOrigin = computed(() =>
    no.value.filter((c) => !canUnplan(c.data, board.value)),
  );
  return { shouldRender, removable: yes, notRemovableUnplannable, notRemovableOrigin };
}

export function useUnplanEligibility(cards: Ref<BoardCard[]>, board: Ref<Board>) {
  const { shouldRender, yes, no } = usePartition(cards, board, canUnplan);
  return { shouldRender, unplannable: yes, notUnplannable: no };
}

export function useDeleteEligibility(cards: Ref<BoardCard[]>, board: Ref<Board>) {
  const isStickyDeletable = (t: BoardType) => useServerSettingsStore().isStickyDeletable(t);
  const isStickyDeletableOnBoard = computed(() => isStickyDeletable(board.value.type));
  const almName = computed(() => getAlmInfo().name);

  // Mirrors in selection — can't be deleted, can only be removed or unplanned
  const blockedMirror = computed(() => cards.value.filter((c) => !isOrigin(c.data, board.value)));

  // ALM origins individually eligible for delete per board config
  const almDeletableOrigin = computed(() =>
    isStickyDeletableOnBoard.value
      ? cards.value.filter((c) => !!c.data.almId && isOrigin(c.data, board.value))
      : [],
  );
  // Multi-ALM bulk forbids delete for all ALM items, even individually-deletable ones
  const isMultiAlmBulk = computed(
    () => cards.value.length > 1 && almDeletableOrigin.value.length > 1,
  );

  const deletable = computed(() =>
    cards.value.filter(
      (c) =>
        canDelete(c.data, board.value, isStickyDeletable) &&
        !(isMultiAlmBulk.value && !!c.data.almId),
    ),
  );
  // Either board-config-blocked OR multi-ALM-blocked — mutually exclusive
  // (board config blocks all ALM uniformly or none).
  const blockedAlm = computed(() =>
    isMultiAlmBulk.value
      ? almDeletableOrigin.value
      : cards.value.filter((c) => isDeleteBlockedByAlm(c.data, board.value, isStickyDeletable)),
  );
  const blockedAlmReason = computed<"multi" | "config">(() =>
    isMultiAlmBulk.value ? "multi" : "config",
  );

  const shouldRender = computed(() => deletable.value.length > 0);
  // Show disabled icon when nothing is deletable and at least one card is ALM-blocked.
  const shouldRenderDisabled = computed(() => !shouldRender.value && blockedAlm.value.length > 0);

  return {
    shouldRender,
    shouldRenderDisabled,
    deletable,
    blockedAlm,
    blockedAlmReason,
    blockedMirror,
    almName,
    isStickyDeletableOnBoard,
  };
}
